import { useEffect } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from './stores/auth'
import { Layout } from './components/Layout/Layout'
import { LoginPage } from './components/Auth/LoginPage'
import { DashboardPage } from './components/Dashboard/DashboardPage'
import { UploadPage } from './components/Upload/UploadPage'
import { JobDetailPage } from './components/JobDetail/JobDetailPage'
import { AdminPage } from './components/Admin/AdminPage'

// ─── Auth Initialization ──────────────────────────────────────────────────────

function AuthProvider({ children }: { children: React.ReactNode }) {
  const fetchUser = useAuthStore((s) => s.fetchUser)
  const handleAuthCallback = useAuthStore((s) => s.handleAuthCallback)
  const isLoading = useAuthStore((s) => s.isLoading)

  useEffect(() => {
    // Restore token from sessionStorage or capture from URL hash
    handleAuthCallback()
    void fetchUser()
  }, [fetchUser, handleAuthCallback])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-alk-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 bg-alk-black flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-alk-red" />
          </div>
          <span className="text-xs font-display uppercase tracking-widest text-alk-gray">
            Loading...
          </span>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// ─── Route Guards ─────────────────────────────────────────────────────────────

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/" replace />
  return <Outlet />
}

function RequireAdmin() {
  const user = useAuthStore((s) => s.user)
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function GuestOnly() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <Outlet />
}

// ─── Auth Callback ────────────────────────────────────────────────────────────
// After magic-link verify, the API redirects here with the JWT in the URL hash.
// AuthProvider's useEffect handles parsing it — this component just redirects.

function AuthCallback() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)

  if (isLoading) return null
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <Navigate to="/" replace />
}

// ─── App ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Auth callback — receives JWT from magic-link redirect */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Public / guest routes */}
          <Route element={<GuestOnly />}>
            <Route path="/" element={<LoginPage />} />
          </Route>

          {/* Protected routes (require auth) */}
          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/upload" element={<UploadPage />} />
              <Route path="/jobs/:id" element={<JobDetailPage />} />

              {/* Admin-only routes */}
              <Route element={<RequireAdmin />}>
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/usage" element={<AdminPage />} />
              </Route>
            </Route>
          </Route>

          {/* Catch-all: redirect authenticated users to dashboard, others to login */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
